import { LoginResponse, TwoFactorAuthLoginVerifyRequestDTO, TwoFactorMethod } from "./interfaces";
import axiosInstance from '../../../../../../utils/axios';
import { JWTPayload } from "./models";
import { decodeJWT } from "../../utils/decodedJWT";
import { CustomApiResponse } from "../../../../../../utils/apiResponse";

/**
 * Normalizes the backend response into a format the Context can easily digest.
 * Following SRP: This service only handles API interaction and data mapping.
 */
export const verifyTwoFactorLogin = async (
  payload: TwoFactorAuthLoginVerifyRequestDTO
): Promise<CustomApiResponse<{ tokens: LoginResponse; user: JWTPayload } | null>> => {
  try {
    // Note: We use <any> here because the received structure can vary (ResponseEntity vs direct DTO)
    const { data: apiResponse } = await axiosInstance.post<CustomApiResponse<any>>(
      '/two-factor-auth/verify',
      payload
    );

    // Case 1: Partial Success (Method validated, but more methods required)
    // If data is null, it indicates a validated intermediate step
    if (apiResponse.status === 'success' && !apiResponse.data) {
      return { status: 'success', data: null, error: null };
    }

    // Case 2: Full Success (Tokens received)
    if (apiResponse.status === 'success' && apiResponse.data) {
      let tokens = apiResponse.data;

      // FIX: Handle the case where the backend returns a serialized ResponseEntity (data.body)
      if (tokens.body && tokens.body.accessToken) {
        tokens = tokens.body;
      }

      // Safety check
      if (!tokens.accessToken) {
        throw new Error('MISSING_ACCESS_TOKEN_IN_RESPONSE');
      }

      const decoded = decodeJWT(tokens.accessToken);

      if (!decoded) throw new Error('JWT_DECODE_FAILED');

      return {
        status: 'success',
        // Force typing here now that we have extracted the correct data
        data: { tokens: tokens as LoginResponse, user: decoded },
        error: null
      };
    }

    return apiResponse as any;
  } catch (error: any) {
    // Error handling (429, 401, etc.) is propagated to the caller
    throw error;
  }
};

export const sendTwoFactorCode = async (email: string) => {
  try {
    await axiosInstance.post('/two-factor-auth/send-code', {
      email,
      method: TwoFactorMethod.EMAIL,
    });
  } catch (error: any) {
    // Handle rate limiting
    if (error?.response?.status === 429) {
      const rateLimitError: any = new Error('Too many attempts. Please try again later.');
      rateLimitError.isRateLimited = true;
      throw rateLimitError;
    }
    throw error;
  }
};
